import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import apiService from '../../services/api';
import '../CardForm.css';
import ReactDOM from 'react-dom';

const RolesList = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [businessId, setBusinessId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: null });

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            setLoading(true);
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.business_id;
            
            // If business_id is undefined, use a default value (you can change this)
            if (!bid) {
                console.warn('Business ID is undefined, using default value 40');
                bid = 40; // Default business_id for testing
            }
            
            setBusinessId(bid);

            console.log('Loading roles for business_id:', bid);
            const response = await apiService.getRoles(bid);
            console.log('Roles API response:', response);
            
            if (response.success && response.data) {
                console.log('Setting roles from response.data:', response.data);
                setRoles(response.data);
            } else if (Array.isArray(response)) {
                console.log('Setting roles from array response:', response);
                setRoles(response);
            } else if (response.data && Array.isArray(response.data)) {
                console.log('Setting roles from response.data array:', response.data);
                setRoles(response.data);
            } else {
                console.log('No roles found in response');
                setRoles([]);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            alert('Failed to load roles: ' + error.message + '\n\nPlease check:\n1. Is your localhost API running?\n2. Does the endpoint exist?\n3. Check browser console for details.');
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (role) => {
        navigate('/roles/add', { state: { role } });
    };

    const handleDelete = async (role) => {
        if (role.is_default) {
            alert('Cannot delete default admin role');
            return;
        }

        if (role.users_count > 0) {
            if (!window.confirm(`This role is assigned to ${role.users_count} user(s). Are you sure you want to delete it?`)) {
                return;
            }
        } else {
            if (!window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
                return;
            }
        }

        try {
            const response = await apiService.deleteRole(role.id);
            if (response.success) {
                alert('Role deleted successfully!');
                loadRoles();
            } else {
                alert(response.message || 'Failed to delete role');
            }
        } catch (error) {
            console.error('Error deleting role:', error);
            alert(error.message || 'Failed to delete role');
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedRoles = (rolesToSort) => {
        if (!sortConfig.key) return rolesToSort;

        return [...rolesToSort].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const toggleDropdown = (roleId, event) => {
        event.stopPropagation();
        
        if (openDropdownId === roleId) {
            setOpenDropdownId(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const dropdownHeight = 120; // Approximate height of dropdown
        const viewportHeight = window.innerHeight;
        
        let top = null;
        let bottom = null;
        
        if (rect.bottom + dropdownHeight <= viewportHeight) {
            top = rect.bottom + window.scrollY;
        } else {
            bottom = viewportHeight - rect.top + window.scrollY;
        }
        
        setDropdownPosition({
            top,
            bottom,
            left: rect.left + window.scrollX
        });
        
        setOpenDropdownId(roleId);
    };

    useEffect(() => {
        const handleClickOutside = () => setOpenDropdownId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Show loading state with uniform UI
    if (loading) {
        return (
            <div className="loading-sales">
                <h3>⏳ Loading Roles...</h3>
                <p>Fetching roles data from server...</p>
            </div>
        );
    }

    const sortedRoles = getSortedRoles(roles);

    return (
        <div>
            <table className="products-table">
                    <thead>
                        <tr>
                            <th>Actions</th>
                            {[
                                { key: 'name', label: 'Role Name' },
                                { key: 'permissions', label: 'Permissions' },
                                { key: 'users_count', label: 'Users' },
                                { key: 'is_default', label: 'Type' }
                            ].map(({ key, label }) => (
                                <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        {label}
                                        {sortConfig.key === key ? (
                                            sortConfig.direction === 'asc' ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                    <path d="M12 19V5M5 12l7-7 7 7" />
                                                </svg>
                                            ) : (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                    <path d="M12 5v14M5 12l7 7 7-7" />
                                                </svg>
                                            )
                                        ) : (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M7 15l5 5 5-5" />
                                                <path d="M7 9l5-5 5 5" />
                                            </svg>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRoles.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                    No roles found. Create your first role to get started.
                                </td>
                            </tr>
                        ) : (
                            sortedRoles.map((role) => (
                                <tr key={role.id}>
                                    <td className="actions-cell">
                                        <div className="action-dropdown-container">
                                            <button
                                                className="action-btn-trigger"
                                                onClick={(e) => toggleDropdown(role.id, e)}
                                            >
                                                Actions <span className="arrow-down">▼</span>
                                            </button>
                                            {openDropdownId === role.id && ReactDOM.createPortal(
                                                <div
                                                    className="action-dropdown-menu"
                                                    style={{
                                                        position: 'fixed',
                                                        top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                        bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                        left: `${dropdownPosition.left}px`,
                                                        zIndex: 1000
                                                    }}
                                                >
                                                    <button onClick={() => handleEdit(role)}>
                                                        <Edit size={14} /> Edit
                                                    </button>
                                                    {!role.is_default && (
                                                        <button onClick={() => handleDelete(role)}>
                                                            <Trash2 size={14} /> Delete
                                                        </button>
                                                    )}
                                                </div>,
                                                document.body
                                            )}
                                        </div>
                                    </td>
                                    <td>{role.name}</td>
                                    <td>{role.permissions?.length || 0} permissions</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                                            <Users size={16} />
                                            {role.users_count || 0}
                                        </div>
                                    </td>
                                    <td>
                                        {role.is_default ? (
                                            <span style={{ 
                                                padding: '0.25rem 0.5rem', 
                                                backgroundColor: '#dbeafe', 
                                                color: '#1e40af', 
                                                borderRadius: '4px', 
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                Default
                                            </span>
                                        ) : (
                                            <span style={{ 
                                                padding: '0.25rem 0.5rem', 
                                                backgroundColor: '#f1f5f9', 
                                                color: '#475569', 
                                                borderRadius: '4px', 
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                Custom
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
        </div>
    );
};

export default RolesList;
